import { useState, type ReactNode } from 'react'
import { Check, ExternalLink, Copy, RefreshCw, SkipForward, Trash2, Ban, Clock } from 'lucide-react'
import { api, type OutreachGroup, type OutreachRecipient, type RecipientMove } from '@/api/client'
import { checkMessage, fmtCount, fmtRate, MESSAGE_MAX, useCreatorMutation, withOptOut } from '@/lib/creators'
import { Button, ConfirmDialog, PlatformMark, Select, StatusPill, When, errorText, useToast } from '@/ui'

export interface CardCtx {
  language: string
  groups: OutreachGroup[]
  nextSendAt: string | null
}

/** Runs one action on a card: busy while it goes, the server's text under the card when it fails. */
function useAct() {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const run = async (name: string, fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(name)
    setError(null)
    try {
      await fn()
      after?.()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(null)
    }
  }
  return { busy, error, run }
}

function Person({ r, ctx, movable }: { r: OutreachRecipient; ctx: CardCtx; movable: boolean }) {
  const c = r.creator
  const post = (c.posts ?? [])[0]
  const move = useCreatorMutation((groupId: number) => api.moveRecipient(r.id, groupId))
  const toast = useToast()
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <PlatformMark platform={c.platform} />
            {c.profile_url ? (
              <a href={c.profile_url} target="_blank" rel="noreferrer" className="truncate font-semibold hover:underline">
                @{c.handle}
              </a>
            ) : (
              <span className="truncate font-semibold">@{c.handle}</span>
            )}
            {c.display_name && <span className="truncate text-table text-muted-foreground">{c.display_name}</span>}
          </div>
          <p className="mt-0.5 text-meta text-muted-foreground tabular-nums">
            팔로워 {fmtCount(c.followers)}
            {c.engagement_rate > 0 && ` · 참여율 ${fmtRate(c.engagement_rate)}`} · 점수 {Math.round(r.score)}
          </p>
        </div>
        {ctx.groups.length > 1 && (
          <Select
            aria-label="그룹 옮기기"
            value={r.group_id}
            disabled={!movable || move.isPending}
            onChange={async (e) => {
              try {
                await move.mutateAsync(Number(e.target.value))
              } catch (err) {
                toast.error(`옮기지 못했다: ${errorText(err)}`)
              }
            }}
            className="h-8 w-auto max-w-[9rem] text-table"
          >
            {ctx.groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </Select>
        )}
      </div>
      {post && (
        <blockquote className="rounded-md bg-muted/60 px-3 py-2 text-table">
          <p className="line-clamp-3 break-words">{post.text || '(글 없음)'}</p>
          {post.url && (
            <a href={post.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-meta text-accent hover:underline">
              근거 글 열기
              <ExternalLink size={12} aria-hidden />
            </a>
          )}
        </blockquote>
      )}
    </div>
  )
}

function Card({ children }: { children: ReactNode }) {
  return <li className="min-w-0 space-y-3 rounded-lg border border-border bg-card p-4">{children}</li>
}

function ErrorLine({ text }: { text: string | null }) {
  return text ? (
    <p role="alert" className="break-words text-table text-status-failed">
      {text}
    </p>
  ) : null
}

/** 승인 대기 — one person, the message written for them, and the five things to do with it. */
export function ApprovalCard({ r, ctx, platform }: { r: OutreachRecipient; ctx: CardCtx; platform: 'instagram' | 'bluesky' }) {
  const m = r.message
  const [body, setBody] = useState(m?.body ?? '')
  const [dnc, setDnc] = useState(false)
  const { busy, error, run } = useAct()
  const toast = useToast()
  const act = useCreatorMutation(async (fn: () => Promise<unknown>) => fn())
  const dirty = body !== (m?.body ?? '')
  const check = checkMessage(body, ctx.language, platform)
  const sendable = !check.empty && check.linkOk && check.lengthOk

  const problems = [
    check.empty && '본문이 비었다.',
    !check.empty && check.links === 0 && '{{link}}가 없다.',
    check.links > 1 && `{{link}}가 ${check.links}번 있다. 한 번만 둔다.`,
    !check.lengthOk && `${MESSAGE_MAX}자를 넘는다.`,
  ].filter(Boolean) as string[]

  const approve = () =>
    run('approve', () =>
      act.mutateAsync(async () => {
        if (dirty) await api.saveRecipientMessage(r.id, withOptOut(body, ctx.language))
        await api.approveRecipientMessage(r.id)
      }),
    )
  const simple = (name: string, fn: () => Promise<unknown>) => run(name, () => act.mutateAsync(fn))

  return (
    <Card>
      <Person r={r} ctx={ctx} movable />
      <div className="space-y-1.5">
        <textarea
          aria-label={`@${r.creator.handle}에게 보낼 메시지`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={7}
          className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
        />
        <div className="flex flex-wrap items-center justify-between gap-2 text-meta text-muted-foreground">
          <span>마지막 줄의 거절 안내는 시스템이 붙인다.</span>
          <span className={check.lengthOk ? 'tabular-nums' : 'tabular-nums text-status-failed'}>
            {check.length} / {MESSAGE_MAX}자
          </span>
        </div>
        {problems.length > 0 && <p className="text-meta text-status-failed">{problems.join(' ')}</p>}
      </div>
      <ErrorLine text={error ?? (m?.error || null)} />
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" icon={Check} loading={busy === 'approve'} disabled={!sendable || !!busy} onClick={() => void approve()}>
          승인
        </Button>
        <Button icon={RefreshCw} loading={busy === 'regen'} disabled={!!busy} onClick={() => void simple('regen', () => api.regenerateRecipientMessage(r.id))}>
          다시 쓰기
        </Button>
        <Button icon={Trash2} loading={busy === 'discard'} disabled={!!busy} onClick={() => void simple('discard', () => api.discardRecipientMessage(r.id))}>
          버리기
        </Button>
        <Button icon={SkipForward} loading={busy === 'skip'} disabled={!!busy} onClick={() => void simple('skip', () => api.transitionRecipient(r.id, 'skip'))}>
          건너뛰기
        </Button>
        <Button variant="dangerOutline" icon={Ban} disabled={!!busy} onClick={() => setDnc(true)}>
          연락 금지
        </Button>
      </div>
      <ConfirmDialog
        open={dnc}
        onOpenChange={setDnc}
        title="연락 금지로 옮길까"
        description={`@${r.creator.handle}은 모든 풀과 모든 발송에서 빠지고, 앞으로 누구도 연락하지 않는다.`}
        confirmLabel="연락 금지"
        confirmVariant="destructive"
        onConfirm={async () => {
          try {
            await act.mutateAsync(() => api.transitionRecipient(r.id, 'do_not_contact'))
            toast.success('연락 금지로 옮겼다')
            setDnc(false)
          } catch (e) {
            toast.error(`옮기지 못했다: ${errorText(e)}`)
          }
        }}
      />
    </Card>
  )
}

/** 보낼 차례 — an approved message. Manual ones are copied and sent by hand; API ones wait for the worker. */
export function QueueCard({ r, ctx }: { r: OutreachRecipient; ctx: CardCtx }) {
  const m = r.message
  const { busy, error, run } = useAct()
  const [dmUrl, setDmUrl] = useState<string | null>(null)
  const toast = useToast()
  const act = useCreatorMutation(async (fn: () => Promise<unknown>) => fn())
  const manual = m?.mode === 'manual'

  const copyAndOpen = () =>
    run('copy', async () => {
      const res = await api.copyRecipientMessage(r.id)
      await navigator.clipboard.writeText(res.body_text)
      setDmUrl(res.dm_url)
      const w = res.dm_url ? window.open(res.dm_url, '_blank', 'noopener,noreferrer') : null
      toast.success(w ? '복사했다. DM 창을 열었다' : '복사했다')
    })

  return (
    <Card>
      <Person r={r} ctx={ctx} movable />
      {m && <p className="whitespace-pre-wrap break-words rounded-md border border-border px-3 py-2 text-table">{m.body}</p>}
      <ErrorLine text={error ?? (m?.error || null)} />
      {manual ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" icon={Copy} loading={busy === 'copy'} disabled={!!busy} onClick={() => void copyAndOpen()}>
            복사하고 DM 열기
          </Button>
          {dmUrl && (
            <a href={dmUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-table text-accent hover:underline">
              DM 창 열기
              <ExternalLink size={12} aria-hidden />
            </a>
          )}
          <Button
            icon={Check}
            loading={busy === 'sent'}
            disabled={!!busy}
            onClick={() => void run('sent', () => act.mutateAsync(() => api.markRecipientSent(r.id)))}
          >
            보냈어요
          </Button>
        </div>
      ) : (
        <p className="inline-flex items-center gap-1.5 text-table text-muted-foreground">
          <Clock size={14} aria-hidden />
          자동 발송 대기
          {ctx.nextSendAt && (
            <>
              <span aria-hidden>·</span>
              다음 발송 <When iso={ctx.nextSendAt} mode="slot" />
            </>
          )}
        </p>
      )}
    </Card>
  )
}

const NEXT: Record<string, { to: RecipientMove; label: string }[]> = {
  sent: [
    { to: 'replied', label: '답장 옴' },
    { to: 'accepted', label: '수락' },
    { to: 'declined', label: '거절' },
  ],
  replied: [
    { to: 'accepted', label: '수락' },
    { to: 'declined', label: '거절' },
  ],
}

/** 보낸 것 — what happened after the message went out. */
export function SentCard({ r, ctx }: { r: OutreachRecipient; ctx: CardCtx }) {
  const { busy, error, run } = useAct()
  const act = useCreatorMutation((to: RecipientMove) => api.transitionRecipient(r.id, to))
  const next = NEXT[r.state] ?? []
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <Person r={r} ctx={ctx} movable={false} />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <StatusPill domain="creator" status={r.state} />
          {r.message?.sent_at && (
            <span className="text-meta text-muted-foreground">
              <When iso={r.message.sent_at} mode="slot" /> 보냄
            </span>
          )}
        </div>
      </div>
      {r.message && <p className="line-clamp-3 whitespace-pre-wrap break-words text-table text-muted-foreground">{r.message.body}</p>}
      <ErrorLine text={error} />
      {next.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {next.map((n) => (
            <Button key={n.to} size="sm" loading={busy === n.to} disabled={!!busy} onClick={() => void run(n.to, () => act.mutateAsync(n.to))}>
              {n.label}
            </Button>
          ))}
        </div>
      )}
    </Card>
  )
}
